/**
 * useTutorCompleteProfileState — les gestes de « Compléter mon profil » et son écriture.
 *
 * Port de `CompleteTutorProfileStepper.js` (et de son `FormStepper`) :
 *  - « Suivant » valide l'étape (les `validationRules` de l'ancien parcours) puis passe à la suivante ;
 *    sur les créneaux, une semaine vide retient (« Ajoute au moins un créneau »), sinon l'engagement
 *    s'ouvre d'abord et « Continuer » avance ;
 *  - le retour recule d'une étape ; de la première, comme la croix, il quitte (la pile, sinon `menu`) ;
 *  - le brouillon est gardé à chaque étape passée et à la sortie, et rendu au prochain passage
 *    (l'ancien `saveFormData`) ; l'envoi réussi l'efface ;
 *  - « Terminer » envoie TOUT le profil, la semaine comprise, en une seule écriture
 *    (`submitCompleteProfile`, mesurée `profile.complete`) : l'ancien parcours n'écrivait rien avant.
 *    Le succès montre l'accusé, « Continuer » quitte ; un refus se dit au-dessus du bouton.
 * Les étapes passées et le retour gardent leurs anciens événements (`form.step.finished`,
 * `button.clicked:progress-stepper.exit`) : les entonnoirs d'aujourd'hui les lisent.
 */
import { useCallback, useEffect, useRef, type Dispatch } from "react"
import { useDispatch } from "react-redux"

import { resetFormData, saveFormData } from "../../../../../../data/redux/actions/formData"
import { tutorsService } from "../../../../../../data/services/tutorsService"
import {
    AVAILABILITY_REQUIRED,
    toCompleteProfilePayload,
    validateTutorProfileStep,
} from "../../../../adapters/tutor/Account/tutorProfileAdapter"
import { TUTOR_CHARTER_URL } from "../../../../adapters/tutor/shared/tutorLinks"
import { continuityDuration } from "../../../../adapters/tutor/shared/tutorWeekAdapter"
import {
    TUTOR_PROFILE_FORM_ID,
    type TutorCompleteProfileViewModel,
} from "../../../../view-models/tutor/Account/tutorCompleteProfileViewModel"
import { track } from "../../../hooks/trackEvent"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"
import { type UITutorHour } from "../../../types/tutorDispoOffres"
import { type UITutorProfileDraft } from "../../../types/tutorCompte"
import {
    neighbourStep,
    type TutorCompleteProfileUiAction,
    type TutorCompleteProfileUiState,
} from "./tutorCompleteProfileUiState"
import type { TutorProfileStep } from "../../../../../../design-system/pages/TutorCompleteProfilePage"
import type { TutorAvailabilityDialogProps } from "../../../../../../design-system/templates/TutorAvailabilityDialog"

const SURFACE = "tutor_complete_profile"

const PROFILE_SUBMIT_FAILED = "Tes informations n'ont pas pu être enregistrées. Réessaie dans un instant."

/** Le nom d'une étape dans `form.step.finished` : le champ de l'ancien formulaire (`formStepName`). */
const LEGACY_STEP_NAME: Record<TutorProfileStep, string> = {
    fixedMissions: "fixed_missions",
    replaceMissions: "replace_missions",
    availability: "availability",
    specialties: "specialty_school_subjects",
    languages: "foreign_languages",
    relevantSubjects: "relevant_subjects",
    classes: "relevant_classes",
    billing: "num_SIRET",
    personalize: "study_field,study_institution,tutor_skill_set_title_id",
    charter: "charter_opt_in",
}

/** La charte se ré-accepte à chaque passage, comme l'ancien : elle n'est pas gardée. */
function withoutCharter(edits: Partial<UITutorProfileDraft>): Partial<UITutorProfileDraft> {
    const kept = { ...edits }
    delete kept.charterAccepted
    return kept
}

export function useTutorCompleteProfileState(
    vm: TutorCompleteProfileViewModel,
    ui: TutorCompleteProfileUiState,
    dispatchUi: Dispatch<TutorCompleteProfileUiAction>,
    retryWeek: () => void
) {
    const dispatch = useDispatch()
    const leave = useBackOr("menu")

    const { start, requesting, gesture } = useApiMutation({
        service: tutorsService.submitCompleteProfile,
        gesture: "profile.complete",
        surface: SURFACE,
        fallback: PROFILE_SUBMIT_FAILED,
        skipWhilePending: true,
        onSuccess: () => {
            if (vm.userId != null) dispatch(resetFormData(TUTOR_PROFILE_FORM_ID, vm.userId) as never)
            dispatchUi({ type: "DONE" })
        },
        onError: (message) => dispatchUi({ type: "SUBMIT_FAILED", message }),
    })

    // Le parcours du geste s'ouvre avec l'écran : une fois.
    const openedRef = useRef(false)
    useEffect(() => {
        if (openedRef.current) return
        openedRef.current = true
        gesture.opened()
    }, [gesture])

    // Une semaine que la lecture n'a pas rapportée se redemande en arrivant sur son étape — une fois
    // par arrivée : une relecture en échec ne boucle pas.
    useEffect(() => {
        if (ui.step === "availability" && vm.weekLoadFailed) retryWeek()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ui.step])

    const keepDraft = useCallback(() => {
        const kept = withoutCharter(vm.keptEdits)
        if (vm.userId == null || Object.keys(kept).length === 0) return
        dispatch(saveFormData(TUTOR_PROFILE_FORM_ID, vm.userId, kept) as never)
    }, [dispatch, vm.userId, vm.keptEdits])

    const advance = useCallback(
        (from: TutorProfileStep) => {
            track({ name: "form.step.finished", properties: { step: LEGACY_STEP_NAME[from] } })
            keepDraft()
            const next = neighbourStep(from, 1)
            if (next) dispatchUi({ type: "GO_TO", step: next })
        },
        [keepDraft, dispatchUi]
    )

    const exit = useCallback(() => {
        if (ui.step !== "done") keepDraft()
        leave()
    }, [ui.step, keepDraft, leave])

    const submit = useCallback(() => {
        if (requesting) return
        dispatchUi({ type: "SUBMITTING" })
        start({}, toCompleteProfilePayload(vm.draft, { openSlots: vm.openSlots, startingDate: vm.startingDate }))
    }, [requesting, start, dispatchUi, vm.draft, vm.openSlots, vm.startingDate])

    const onNext = useCallback(() => {
        const step = ui.step
        if (step === "done" || requesting) return
        if (step === "availability") {
            if (vm.weekSlotCount === 0) dispatchUi({ type: "INVALID", errors: { availability: AVAILABILITY_REQUIRED } })
            else dispatchUi({ type: "OPEN_CONTINUITY" })
            return
        }
        const errors = validateTutorProfileStep(step, vm.draft)
        if (Object.keys(errors).length > 0) {
            dispatchUi({ type: "INVALID", errors })
            return
        }
        if (step === "charter") {
            track({ name: "form.step.finished", properties: { step: LEGACY_STEP_NAME.charter } })
            submit()
            return
        }
        advance(step)
    }, [ui.step, requesting, vm.weekSlotCount, vm.draft, dispatchUi, submit, advance])

    const onBack = useCallback(() => {
        track({ name: "button.clicked:progress-stepper.exit" })
        const previous = ui.step === "done" ? null : neighbourStep(ui.step, -1)
        if (previous == null) {
            exit()
            return
        }
        keepDraft()
        dispatchUi({ type: "GO_TO", step: previous })
    }, [ui.step, exit, keepDraft, dispatchUi])

    const closeDialog = () => dispatchUi({ type: "CLOSE_DIALOG" })
    let dialog: TutorAvailabilityDialogProps | null = null
    if (ui.dialog?.kind === "continuity") {
        dialog = {
            visible: true,
            onClose: closeDialog,
            kind: "continuity",
            mode: "continue",
            duration: continuityDuration(),
            onConfirm: () => {
                closeDialog()
                advance("availability")
            },
        }
    } else if (ui.dialog?.kind === "live") {
        dialog = { visible: true, onClose: closeDialog, kind: "live", liveTimesLabel: ui.dialog.liveTimesLabel }
    }

    return {
        onChange: (patch: Partial<UITutorProfileDraft>) => dispatchUi({ type: "CHANGE", patch }),
        onNext,
        onBack,
        onClose: () => {
            track({ name: "button.clicked:progress-stepper.exit" })
            exit()
        },
        onDone: leave,
        onPressHour: (hour: UITutorHour) =>
            dispatchUi({ type: "PRESS_HOUR", hour, serverOpenSlots: vm.serverOpenSlots }),
        onPickStartingDate: (date: string) => dispatchUi({ type: "PICK_STARTING_DATE", date }),
        charterUrl: TUTOR_CHARTER_URL,
        submitting: requesting,
        dialog,
    }
}
